/**
 * Keeps the two side rails of a brand takeover pinned beside the page's content
 * surface: they follow the top of that surface while it is on screen, stick to the
 * viewport top once it has scrolled past, and are lifted so they never overlap the
 * footer.
 *
 * Shared by every takeover on the site, so it is driven entirely by data attributes
 * rather than class names -- the older `a-campaign` markup and the newer `a-takeover`
 * markup have different classes and different creative sizes, but identical scroll
 * behaviour. A takeover opts in with:
 *
 *   <div data-rails
 *        data-anchor=".home-page" data-anchor-gap="0"
 *        data-footer=".home-page-footer">
 *       <... data-rail="left">  <... data-rail="right">
 *
 * Both selectors are markup-supplied because they differ per page -- the home page
 * names its footer `.home-page-footer`, the rest of the site `.footer`, and rails that
 * silently stopped moving would overlap whichever one they could not find.
 * `data-anchor-gap` is optional and likewise per-page: the home page rails sit flush
 * with its content card, the older takeover keeps its original offset.
 *
 * Only `top` is written; the easing comes from the CSS transition on the rails.
 *
 * Naming note: this ships as `a-campaign.js`, with no "ad" stem in the path, because
 * filter lists match script URLs as readily as class names.
 */

const VIEWPORT_GAP = 24;
const FALLBACK_RAIL_HEIGHT = 600;

const root = document.querySelector<HTMLElement>('[data-rails]');
const leftRail = root?.querySelector<HTMLElement>('[data-rail="left"]') ?? null;
const rightRail = root?.querySelector<HTMLElement>('[data-rail="right"]') ?? null;
const anchorSelector = root?.dataset.anchor ?? '';
const footerSelector = root?.dataset.footer ?? '';
const anchor = anchorSelector ? document.querySelector<HTMLElement>(anchorSelector) : null;
const footer = footerSelector ? document.querySelector<HTMLElement>(footerSelector) : null;

// How far below the surface's top edge the rails rest. 0 lines them up with it exactly;
// the default keeps the older takeover's original offset. Number(undefined) is NaN, so a
// missing or malformed attribute falls back rather than poisoning the arithmetic.
const declaredAnchorGap = Number(root?.dataset.anchorGap);
const anchorGap = Number.isFinite(declaredAnchorGap) ? declaredAnchorGap : VIEWPORT_GAP;

const positionRails = (): void => {
    if (!leftRail || !rightRail || !anchor) {
        return;
    }

    // Both rails run the same creative size by contract, so one measurement does.
    const railHeight = leftRail.offsetHeight || FALLBACK_RAIL_HEIGHT;
    const anchorRect = anchor.getBoundingClientRect();
    const footerRect = footer?.getBoundingClientRect();
    const isAnchorVisible = anchorRect.top < window.innerHeight && anchorRect.bottom > 0;

    // The rails rest `anchorGap` below the surface's top edge -- 0 on the home page, so
    // they line up with the content card exactly. VIEWPORT_GAP is the floor: once that
    // point has scrolled above it, the rails park there rather than riding the very top
    // of the window.
    let targetTop = isAnchorVisible
        ? Math.max(VIEWPORT_GAP, anchorRect.top + anchorGap)
        : VIEWPORT_GAP;

    if (footerRect && footerRect.top < window.innerHeight) {
        // The footer is in view: lift the rails clear of it, but never so far that they
        // hang below the fold.
        targetTop = Math.min(targetTop, footerRect.top - railHeight - VIEWPORT_GAP);
        targetTop = Math.min(targetTop, window.innerHeight - railHeight);
    }

    leftRail.style.top = `${targetTop}px`;
    rightRail.style.top = `${targetTop}px`;
};

window.addEventListener('scroll', positionRails, { passive: true });
window.addEventListener('resize', positionRails, { passive: true });

positionRails();

export {};
